/**
 * MapBottomSheet - Map + draggable bottom sheet in a flex column layout
 * Map and sheet are flex siblings — no absolute positioning needed
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Day } from '../../types/trip';
import type { Activity, ActivityType } from '../../types/activity';
import TripMapView from './TripMapView';
import ActivityCard from '../activity/ActivityCard';
import SortableActivityCard from '../activity/SortableActivityCard';
import ActivityTypeFilter from '../activity/ActivityTypeFilter';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatDateWithDay } from '../../utils/dates';

// Map height as fraction of the combined container height
const FRAC_DEFAULT  = 0.60; // default: 60% map / 40% list
const FRAC_EXPANDED = 0.25; // pulled up: 25% map / 75% list
const FRAC_FULL     = 0.00; // fully expanded: list covers map entirely

interface MapBottomSheetProps {
  day: Day;
  onEditActivity: (activity: Activity) => void;
  onDeleteActivity: (id: string) => void;
  onReorderActivity?: (activityId: string, newIndex: number) => void;
  onMoveToDay?: (activityId: string, dayIndex: number) => void;
  onTogglePrimary?: (activity: Activity) => void;
  availableDays?: Day[];
  /** 地圖是否顯示順序編號與路線（由頁面持有，和桌面版開關同步） */
  showRoute?: boolean;
  onShowRouteChange?: (showRoute: boolean) => void;
}

export default function MapBottomSheet({ day, onEditActivity, onDeleteActivity, onReorderActivity, onMoveToDay, onTogglePrimary, availableDays, showRoute = true, onShowRouteChange }: MapBottomSheetProps) {
  const navigate = useNavigate();
  const { id: tripId } = useParams<{ id: string }>();
  const [mapFrac, setMapFrac] = useState(FRAC_DEFAULT);
  const [isDragging, setIsDragging] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState<Activity | undefined>();
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [activeActivityId, setActiveActivityId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ActivityType[]>([]);

  const filteredActivities = useMemo(() => {
    if (typeFilter.length === 0) return day.activities;
    return day.activities.filter((a) => a.type && typeFilter.includes(a.type));
  }, [day.activities, typeFilter]);
  const isFiltered = typeFilter.length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveActivityId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveActivityId(null);
    if (!over || active.id === over.id) return;
    const activities = day.activities;
    const oldIndex = activities.findIndex((a) => a.id === active.id);
    const newIndex = activities.findIndex((a) => a.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderActivity?.(String(active.id), newIndex);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const startFrac = useRef(FRAC_DEFAULT);

  const onPointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startFrac.current = mapFrac;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const h = containerRef.current?.clientHeight ?? window.innerHeight;
    const delta = (e.clientY - startY.current) / h;
    setMapFrac(Math.max(FRAC_FULL, Math.min(0.85, startFrac.current + delta)));
  };

  const onPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    // 三段 snap：FULL(0) / EXPANDED(0.25) / DEFAULT(0.60)
    if (mapFrac < (FRAC_FULL + FRAC_EXPANDED) / 2) {
      setMapFrac(FRAC_FULL);
    } else if (mapFrac < (FRAC_EXPANDED + FRAC_DEFAULT) / 2) {
      setMapFrac(FRAC_EXPANDED);
    } else {
      setMapFrac(FRAC_DEFAULT);
    }
  };

  const handleCardClick = (activity: Activity) => {
    navigate(`/trips/${tripId}/itinerary/${activity.id}?day=${day.dayNumber}`);
  };

  return (
    <div ref={containerRef} className="flex-1 flex flex-col min-h-0">
      {/* Map section */}
      <div
        className="relative shrink-0 overflow-hidden"
        style={{
          height: `${mapFrac * 100}%`,
          transition: isDragging ? 'none' : 'height 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <TripMapView
          activities={filteredActivities}
          selectedActivityId={selectedActivityId}
          onMarkerClick={(id) => setSelectedActivityId(id || null)}
          onMapClick={() => setSelectedActivityId(null)}
          showRoute={showRoute}
        />

        {onShowRouteChange && (
          <label className="absolute top-3 right-3 z-10 flex items-center gap-2 rounded-full bg-white/95 pl-3 pr-1.5 py-1.5 shadow-md cursor-pointer select-none">
            <span className="text-xs font-medium text-slate-600">順序與路線</span>
            <button
              type="button"
              role="switch"
              aria-checked={showRoute}
              onClick={() => onShowRouteChange(!showRoute)}
              className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
              style={{ background: showRoute ? '#0dccf2' : '#E5E7EB' }}
            >
              <span
                className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform"
                style={{ transform: showRoute ? 'translateX(18px)' : 'translateX(3px)' }}
              />
            </button>
          </label>
        )}
      </div>

      {/* Bottom sheet */}
      <div
        className="flex-1 flex flex-col min-h-0 bg-white rounded-t-2xl"
        style={{ boxShadow: '0 -4px 24px rgba(0,0,0,0.10)' }}
      >
        {/* Drag handle */}
        <div
          className="flex justify-center py-3 cursor-grab active:cursor-grabbing select-none touch-none shrink-0"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Day header */}
        <div className="flex items-center justify-between px-5 pb-4 shrink-0">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[22px] font-semibold text-slate-900 tracking-tight">
              Day {day.dayNumber}
            </h2>
            <p className="text-[13px] text-slate-400 font-medium">
              {formatDateWithDay(day.date)} • {filteredActivities.length} 個行程
            </p>
          </div>
          <ActivityTypeFilter selectedTypes={typeFilter} onChange={setTypeFilter} />
        </div>

        {/* Scrollable activity list */}
        <div className="flex-1 overflow-y-auto px-5 pb-10 min-h-0">
          {filteredActivities.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-10">
              {isFiltered ? '沒有符合篩選條件的行程' : '這天還沒有行程'}
            </p>
          ) : isFiltered ? (
            /* 篩選中：停用拖曳排序，避免以子集合的順序誤蓋掉當天完整順序 */
            <div className="flex flex-col">
              {filteredActivities.map((activity, index) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  index={index}
                  onClick={() => handleCardClick(activity)}
                  onEdit={() => onEditActivity(activity)}
                  onDelete={() => setDeletingActivity(activity)}
                  onMoveToDay={onMoveToDay ? (dayIndex) => onMoveToDay(activity.id, dayIndex) : undefined}
                  onTogglePrimary={onTogglePrimary ? () => onTogglePrimary(activity) : undefined}
                  availableDays={availableDays}
                />
              ))}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={day.activities.map((a) => a.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col">
                  {day.activities.map((activity, index) => (
                    <SortableActivityCard
                      key={activity.id}
                      activity={activity}
                      index={index}
                      onClick={() => handleCardClick(activity)}
                      onEdit={() => onEditActivity(activity)}
                      onDelete={() => setDeletingActivity(activity)}
                      onMoveToDay={onMoveToDay ? (dayIndex) => onMoveToDay(activity.id, dayIndex) : undefined}
                      onTogglePrimary={onTogglePrimary ? () => onTogglePrimary(activity) : undefined}
                      availableDays={availableDays}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeActivityId ? (
                  <ActivityCard
                    activity={day.activities.find((a) => a.id === activeActivityId)!}
                    index={day.activities.findIndex((a) => a.id === activeActivityId)}
                    variant="scheduled"
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deletingActivity}
        title="刪除行程"
        message={`確定要刪除「${deletingActivity?.name}」嗎？`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={() => {
          if (deletingActivity) {
            onDeleteActivity(deletingActivity.id);
            setDeletingActivity(undefined);
            setSelectedActivityId(null);
          }
        }}
        onClose={() => setDeletingActivity(undefined)}
      />
    </div>
  );
}
